import React, { useState } from 'react'
import { T } from '../ui'
import { askNFL } from '../api'
import { playText, teamNick } from '../plain'

// Ask BRKR — a research box over the model's board. Type a player, market, team, tier, or
// "how are we doing" and it returns the matching model plays / record. Research only: it
// surfaces calibrated probabilities, never a parlay or a bet instruction.
const SUGGEST = ['Best plays this week', 'Elite plays', 'How do I read this?', 'Top receptions', 'How are we doing']
const pctInt = p => `${Math.round((p || 0) * 100)}%`
const probColor = p => p >= 0.66 ? T.green : p >= 0.58 ? T.green2 || T.green : p >= 0.5 ? T.text : T.muted
const TIER_C = { ELITE: T.green, 'BEST BET': T.green, STRONG: T.blue || T.text }

function PlayRow({ p, onPlayer }) {
  const isTD = p.market === 'Anytime TD'
  return (
    <button onClick={() => onPlayer && p.gsis_id && onPlayer({ gsis_id: p.gsis_id, full_name: p.name, position: p.pos, team: p.team, opponent: p.opp })}
      style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, textAlign: 'left', cursor: onPlayer ? 'pointer' : 'default',
        background: 'transparent', border: 'none', borderTop: `1px solid ${T.border}`, padding: '9px 2px', color: T.text }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
        <div style={{ fontSize: 10.5, color: T.muted }}>
          {[p.pos, p.team, p.opp ? `vs ${teamNick(p.opp)}` : ''].filter(Boolean).join(' · ')} · {isTD ? 'Anytime TD' : playText(p.market, p.line)}
          {p.td_prob != null ? <span style={{ color: T.green }}> · {Math.round(p.td_prob * 100)}% to score</span> : null}
          {p.edge_pct != null && p.edge_pct > 0 ? <span style={{ color: T.green }}> · +{Math.round(p.edge_pct)}%</span> : null}
        </div>
      </div>
      {p.tier ? <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.03em', color: TIER_C[p.tier] || T.muted,
        border: `1px solid ${TIER_C[p.tier] || T.border}`, borderRadius: 5, padding: '2px 5px', whiteSpace: 'nowrap' }}>{p.tier}</span> : null}
      <span style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 14, color: probColor(p.prob || 0), minWidth: 42, textAlign: 'right' }}>{pctInt(p.prob)}</span>
    </button>
  )
}

export default function AskBrkr({ season, week, onPlayer }) {
  const [q, setQ] = useState('')
  const [res, setRes] = useState(null)
  const [busy, setBusy] = useState(false)

  const run = async query => {
    const text = (query != null ? query : q).trim()
    if (!text || busy) return
    setQ(text); setBusy(true)
    try { setRes(await askNFL(text, season || 2026, week || 1)) }
    catch (_) { setRes({ type: 'empty', answer: 'Could not reach the board right now — try again shortly.', plays: [] }) }
    setBusy(false)
  }

  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 15, fontWeight: 900 }}>Ask <span style={{ color: T.green }}>BRKR</span></span>
        <span style={{ fontSize: 10.5, color: T.muted }}>the model, in plain English</span>
      </div>
      <div style={{ fontSize: 11, color: T.muted, marginBottom: 8, lineHeight: 1.4 }}>
        New here? Type a question or tap one below — new to props, tap <b style={{ color: T.text }}>"How do I read this?"</b>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') run() }}
          placeholder="try 'Nacua over 80 yards and a TD'…" style={{ flex: 1, minWidth: 0, boxSizing: 'border-box',
            background: T.card2, border: `1px solid ${T.border}`, borderRadius: 9, padding: '10px 12px', color: T.text, fontSize: 13.5, fontFamily: 'inherit' }} />
        <button onClick={() => run()} disabled={busy} style={{ flexShrink: 0, padding: '0 16px', borderRadius: 9, cursor: busy ? 'default' : 'pointer',
          fontSize: 13.5, fontWeight: 800, border: 'none', background: T.green, color: '#04140D', opacity: busy ? 0.6 : 1 }}>{busy ? '…' : 'Ask'}</button>
      </div>

      {!res && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          {SUGGEST.map(s => (
            <button key={s} onClick={() => run(s)} style={{ cursor: 'pointer', fontSize: 11.5, fontWeight: 700, padding: '5px 10px',
              borderRadius: 8, border: `1px solid ${T.border}`, background: T.card2, color: T.muted }}>{s}</button>
          ))}
        </div>
      )}

      {res && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 13, color: T.text, fontWeight: 600, lineHeight: 1.45, background: T.greenDim,
            border: `1px solid ${T.greenMid || T.green}`, borderRadius: 9, padding: '9px 12px' }}>{res.answer}</div>
          {(res.plays || []).length > 0 && (
            <div style={{ marginTop: 4 }}>
              {res.plays.map((p, i) => <PlayRow key={i} p={p} onPlayer={onPlayer} />)}
            </div>
          )}
          {(res.plays || []).length > 0 && (
            <div style={{ fontSize: 10, color: T.faint, marginTop: 6, lineHeight: 1.4 }}>
              The <b style={{ color: T.muted }}>%</b> is the model's chance the play hits · tap a name for the full breakdown.
            </div>
          )}
          {res.matchup && (res.matchup.defenses || []).length > 0 && (
            <div style={{ marginTop: 4 }}>
              {res.matchup.defenses.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 2px', borderTop: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700 }}>{i + 1}. {d.team}</span>
                  <span style={{ fontFamily: T.mono, fontSize: 13, color: T.text }}>{d.value}
                    <span style={{ color: T.faint, fontSize: 10, marginLeft: 4 }}>{d.metric}</span></span>
                </div>
              ))}
              <div style={{ fontSize: 10, color: T.faint, marginTop: 6, lineHeight: 1.4 }}>
                Defense giving up the most to {res.matchup.position}s per game (softest matchup first) · research.
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <button onClick={() => { setRes(null); setQ('') }} style={{ cursor: 'pointer', fontSize: 11, fontWeight: 700,
              color: T.green, background: 'none', border: 'none', padding: 0 }}>← Ask something else</button>
            <span style={{ fontSize: 10, color: T.faint }}>Model probabilities · research, not advice · 21+</span>
          </div>
        </div>
      )}
    </div>
  )
}
